import { useMemo } from 'react';
import { isTauri } from '../../utils/tauri';
import { MAX_KEY_FILE_BYTES } from './vaultItemData';

/** Field edits, the SSH key file picker and the type pick for the vault item modal. */
export function useVaultKeyFile({
  t,
  folders,
  setStep,
  setType,
  setValues,
  setKeyFileName,
  setKeyFileError,
  keyFileInputRef,
  seedPersonalFolder,
}) {
  const setValue = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  const applyKeyFile = (name, text) => {
    // A NUL byte means a binary pick (disk image, .pfx, …) — every real key
    // format (OpenSSH, PEM, PuTTY .ppk) is plain text.
    if (text.includes('\u0000')) {
      setKeyFileError(t('vault.keyFileBinary', 'That file is not a text key file.'));
      return;
    }
    setValue('privateKey', text);
    setKeyFileName(name);
    setKeyFileError(null);
  };

  // Same dialog-or-input split as VaultImportExportModal.pickFile: the Tauri
  // dialog adds the picked path to the fs scope, the browser falls back to a
  // hidden <input type="file">. No extension filter — key files usually have
  // none (id_rsa, id_ed25519).
  const pickKeyFile = async () => {
    setKeyFileError(null);
    if (isTauri()) {
      try {
        const { open } = await import('@tauri-apps/plugin-dialog');
        const { readTextFile, stat } = await import('@tauri-apps/plugin-fs');
        const selected = await open({ multiple: false });
        if (selected == null) return;
        const path = Array.isArray(selected) ? selected[0] : selected;
        const info = await stat(path).catch(() => null);
        if (info && info.size > MAX_KEY_FILE_BYTES) {
          setKeyFileError(t('vault.keyFileTooLarge', 'That file is too large to be a private key.'));
          return;
        }
        const text = await readTextFile(path);
        applyKeyFile(String(path).split(/[\\/]/).pop() || String(path), text);
      } catch (e) {
        setKeyFileError(String((e && e.message) || e));
      }
      return;
    }
    keyFileInputRef.current?.click();
  };

  const onKeyFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (file.size > MAX_KEY_FILE_BYTES) {
      setKeyFileError(t('vault.keyFileTooLarge', 'That file is too large to be a private key.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => applyKeyFile(file.name, String(reader.result || ''));
    reader.onerror = () => setKeyFileError(t('vault.keyFileReadError', 'Could not read that file.'));
    reader.readAsText(file);
  };

  const pickType = (picked) => {
    setType(picked);
    setStep('form');
  };

  // Switching sftp/ftp/ftps re-defaults the port only while it still holds
  // the previous protocol's default (a hand-typed port is kept).
  // "Create new folder…" is a sentinel value, never a real folder name — a
  // folder literally called that would otherwise be unselectable. Picking it
  // reveals an inline input rather than opening a second modal, which is what
  // the owner asked for.
  const NEW_FOLDER = '__apiboo_new_folder__';
  // Passed in rather than read from the store: the parent already holds the
  // item list this is derived from, and reaching into the store here would make
  // the modal untestable without standing the whole store up — which is exactly
  // what broke when it was written that way.
  //
  // A PRE-FILED folder is unioned in. `folders` is derived from the item list,
  // so a folder that exists but is EMPTY (registered in `__folders__`, holding
  // nothing yet) is not in it — and an empty folder is precisely the one a user
  // clicks "+" on. Without this the picker would show a value it has no option
  // for, and the item would look unfiled.
  const existingFolders = useMemo(() => {
    const base = Array.isArray(folders) ? folders : [];
    if (!seedPersonalFolder || base.some((f) => f?.toLowerCase() === seedPersonalFolder.toLowerCase())) {
      return base;
    }
    return [...base, seedPersonalFolder].sort((a, b) => a.localeCompare(b));
  }, [folders, seedPersonalFolder]);

  return { setValue, pickKeyFile, onKeyFileInputChange, pickType, NEW_FOLDER, existingFolders };
}
